import React,
{
    useEffect,
    useRef,
    useState
}
from 'react';
import
{
    navigate
}
from './Router';
import masterVolume from './masterVolume';
import
{
    createPreloadedAudio
}
from './audioPreload';
import
{
    isHalloweenMode
}
from './seasonalMode';

// base refrences for custom positioning of imageFloof and bgImage on the right side of the screen
// using Ari as the main reference point since hes the only one with both bg and floof images set to 0,0. 
// these are used to calculate the final position of the images based on the chosen goobers x/y offsets
// which allows for consistent positioning across different goobers even if their assets are different sizes
const ARI_FLOOF_REFERENCE_RIGHT_RATIO = 0.01;
const ARI_FLOOF_REFERENCE_TOP_RATIO = 0.6;
const ARI_FLOOF_REFERENCE_HEIGHT_RATIO = 1.13;
const BG_IMAGE_REFERENCE_RIGHT_RATIO = 0;
const BG_IMAGE_HEIGHT_RATIO = 100 / 110;
const BG_IMAGE_TOP_RATIO = 0.5 - (0.1 / 1.1);
// FUCK THIS BUTTON SCALING WHY WAS IT SCALING INDIVIDUALLY
const BASE_BUTTON_LIST_WIDTH = 380;
const BASE_BUTTON_LIST_HEIGHT = 904;
const BASE_BUTTON_GAP = 12;
const BUTTON_LIST_LEFT_OFFSET = '30px'; // text with 'vw' eventually
const MOBILE_WIDTH_DELTA_TOLERANCE = 2;
const MOBILE_HEIGHT_DELTA_TOLERANCE = 120;
const MAIN_EXIT_DURATION_MS = 380;
const HALLOWEEN_MODE = isHalloweenMode();

const MAIN_BUTTON_EXIT_DURATION_MS = 400;
const QUALITIES_TEXT_ROTATION_DELAY_MS = 650;
const QUALITIES_TEXT_ROTATION_RANGE_DEG = 5;

// testing, might deprecate
const MAIN_BUTTON_HOVER_AUDIO_SRC = '/Audio/Main-ButtonHover.ogg';

function getRandomRotationDeg(rangeDeg = QUALITIES_TEXT_ROTATION_RANGE_DEG)
{
    const safeRange = Number.isFinite(rangeDeg) ? Math.max(0, rangeDeg) : 0;
    return (Math.random() * safeRange * 2) - safeRange;
}

function readViewportSize()
{
    return {
        width: Math.round(window.innerWidth),
        height: Math.round(window.innerHeight),
    };
}

function shouldSkipViewportUpdate(previousSize, nextSize)
{
    const widthDelta = Math.abs(previousSize.width - nextSize.width);
    const heightDelta = Math.abs(previousSize.height - nextSize.height);

    if (widthDelta === 0 && heightDelta === 0)
    {
        return true;
    }

    let isCoarsePointer = false;
    try
    {
        isCoarsePointer = window.matchMedia('(pointer: coarse)').matches;
    }
    catch (e)
    {}

    if (!isCoarsePointer)
    {
        return false;
    }

    const orientationChanged = (previousSize.width > previousSize.height) !== (nextSize.width > nextSize.height);

    if (orientationChanged)
    {
        return false;
    }

    if (widthDelta > MOBILE_WIDTH_DELTA_TOLERANCE)
    {
        return false;
    }

    return heightDelta <MOBILE_HEIGHT_DELTA_TOLERANCE;
}

export default function Main()
{
    // Editable per-button stagger delay (seconds)
    const BUTTON_AppearDelay = 0.1;
    const ABOUT_ME_AUDIO_CONFIG_URL = '/aboutMeAudio.json';
    const FULL_MOON_CLUB_AUDIO_CONFIG_URL = '/fullMoonClubAudio.json';
    const ABOUT_ME_IMAGE_SOURCES = [
        '/AboutMe Images/General.png',
        '/AboutMe Images/Offline.png',
        '/AboutMe Images/Online.png',
        '/AboutMe Images/Games.png',
        '/AboutMe Images/Secret.png',
        '/AboutMe Images/GeneralBG-Tiles.png',
    ];

    const [visible, setVisible] = useState(false);
    const [animate, setAnimate] = useState(false);
    const [goobers, setGoobers] = useState([]);
    const [chosen, setChosen] = useState(null);
    const [leaving, setLeaving] = useState(false);
    const [viewportSize, setViewportSize] = useState(() => readViewportSize());
    const [qualitiesTextRotations, setQualitiesTextRotations] = useState([0, 0, 0]);
    const hoverAudioRef = useRef(null);
    const qualitiesRotationTimeoutRef = useRef(null);
    const isCoarsePointer = window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
    const hasBgImage = Boolean(chosen && chosen.bgImage);
    const hasFloofImage = Boolean(chosen && chosen.floofImage);
    const hasCustomNameImage = Boolean(chosen && chosen.customNameImage && chosen.nameImage);
    const showAssetStage = hasBgImage || hasFloofImage;
    const chosenXOffset = chosen ? Number(chosen.xOffset) : NaN;
    const chosenYOffset = chosen ? Number(chosen.yOffset) : NaN;
    const chosenNameSize = chosen ? Number(chosen.nameSize) : NaN;
    const hasNameSizeOverride = Number.isFinite(chosenNameSize) && chosenNameSize > 0;
    const chosenName = chosen && chosen.name ? chosen.name : '';
    const chosenText1 = chosen && typeof chosen.text1 === 'string' ? chosen.text1.trim() : '';
    const chosenText2 = chosen && typeof chosen.text2 === 'string' ? chosen.text2.trim() : '';
    const chosenText3 = chosen && typeof chosen.text3 === 'string' ? chosen.text3.trim() : '';
    const chosenQualitiesBg = chosen && chosen.colorBG ? chosen.colorBG : '#a9dfff';
    const assetXOffset = Number.isFinite(chosenXOffset) ? chosenXOffset : 0;
    const assetYOffset = Number.isFinite(chosenYOffset) ? chosenYOffset : 0;
    const nameAssetScale = hasNameSizeOverride ? chosenNameSize : 1;
    const qualitiesTextItems = [chosenText1, chosenText2, chosenText3].filter(Boolean);
    const ariReference = goobers.find((g) => g && g.name === 'Ari') || null;
    const ariReferenceXOffset = ariReference ? Number(ariReference.xOffset) : NaN;
    const ariReferenceYOffset = ariReference ? Number(ariReference.yOffset) : NaN;
    const floofBaseXOffset = Number.isFinite(ariReferenceXOffset) ? ariReferenceXOffset : 0;
    const floofBaseYOffset = Number.isFinite(ariReferenceYOffset) ? ariReferenceYOffset : 0;
    const floofXOffset = chosen && chosen.name === 'Ari' ? assetXOffset : (floofBaseXOffset + assetXOffset);
    const floofYOffset = chosen && chosen.name === 'Ari' ? assetYOffset : (floofBaseYOffset + assetYOffset);
    const availableButtonHeight = viewportSize.height - 32;
    const availableButtonWidth = viewportSize.width * 0.32;
    const buttonListScale = Math.min(availableButtonHeight / BASE_BUTTON_LIST_HEIGHT, availableButtonWidth / BASE_BUTTON_LIST_WIDTH);
    const scaledButtonListWidth = BASE_BUTTON_LIST_WIDTH * buttonListScale;
    const scaledButtonListHeight = BASE_BUTTON_LIST_HEIGHT * buttonListScale;
    const scaledButtonGap = BASE_BUTTON_GAP * buttonListScale;
    const scaledButtonPaddingY = 16 * buttonListScale;
    const scaledButtonPaddingX = 42 * buttonListScale;
    const scaledButtonFontSizePx = 19.2 * buttonListScale;
    const scaledButtonLetterSpacingPx = 0.6 * buttonListScale;
    const assetStageWidth = viewportSize.width;
    const assetStageHeight = viewportSize.height;
    const floofReferenceRightPx = viewportSize.width * ARI_FLOOF_REFERENCE_RIGHT_RATIO;
    const floofReferenceTopPx = viewportSize.height * ARI_FLOOF_REFERENCE_TOP_RATIO;
    const floofReferenceHeightPx = viewportSize.height * ARI_FLOOF_REFERENCE_HEIGHT_RATIO;
    const bgReferenceRightPx = viewportSize.width * BG_IMAGE_REFERENCE_RIGHT_RATIO;
    const baseNameAssetWidthPx = Math.max(240, Math.min(assetStageWidth * 0.3, 420));
    const baseNameAssetHeightPx = Math.max(72, Math.min(assetStageHeight * 0.12, 132));
    const scaledNameAssetWidthPx = Math.max(180, Math.round(baseNameAssetWidthPx * nameAssetScale));
    const scaledNameAssetHeightPx = Math.max(48, Math.round(baseNameAssetHeightPx * nameAssetScale));
    const qualitiesTextFontSizePx = Math.max(12, Math.min(assetStageWidth * 0.016, 22));
    const qualitiesTextPaddingY = Math.max(6, Math.min(assetStageHeight * 0.008, 11));
    const qualitiesTextPaddingX = Math.max(12, Math.min(assetStageWidth * 0.012, 18));
    const qualitiesRowMaxWidthPx = Math.max(220, scaledNameAssetWidthPx);
    const qualitiesRowGapPx = Math.max(8, Math.min(assetStageWidth * 0.01, 16));
    // Fixed badge size - no scaling based on text length
    const fixedQualitiesTextFontSizePx = qualitiesTextFontSizePx;
    const fixedQualitiesTextPaddingY = qualitiesTextPaddingY;
    const fixedQualitiesTextPaddingX = qualitiesTextPaddingX;
    const fixedQualitiesRowGapPx = qualitiesRowGapPx;

    useEffect(() =>
    {
        // fade in when mounted
        const t = setTimeout(() => setVisible(true), 30);
        const a = setTimeout(() => setAnimate(true), 60);
        return () =>
        {
            clearTimeout(t);
            clearTimeout(a);
        };
    }, []);

    useEffect(() =>
    {
        const rotationTextItems = [chosenText1, chosenText2, chosenText3].filter(Boolean);

        const clearRotationTimeout = () =>
        {
            if (qualitiesRotationTimeoutRef.current)
            {
                try
                {
                    clearTimeout(qualitiesRotationTimeoutRef.current);
                }
                catch (e)
                {}
                qualitiesRotationTimeoutRef.current = null;
            }
        };

        clearRotationTimeout();

        if (!rotationTextItems.length)
        {
            setQualitiesTextRotations([0, 0, 0]);
            return () => clearRotationTimeout();
        }

        setQualitiesTextRotations(() => rotationTextItems.map(() => getRandomRotationDeg()));

        const tick = () =>
        {
            setQualitiesTextRotations(() => rotationTextItems.map(() => getRandomRotationDeg()));
            qualitiesRotationTimeoutRef.current = setTimeout(tick, QUALITIES_TEXT_ROTATION_DELAY_MS);
        };

        qualitiesRotationTimeoutRef.current = setTimeout(tick, QUALITIES_TEXT_ROTATION_DELAY_MS);

        return () => clearRotationTimeout();
    }, [chosenName, chosenText1, chosenText2, chosenText3]);

    useEffect(() =>
    {
        let resizeCommitTimer = null;

        const onResize = () =>
        {
            try
            {
                if (resizeCommitTimer) clearTimeout(resizeCommitTimer);
                resizeCommitTimer = setTimeout(() =>
                {
                    resizeCommitTimer = null;
                    setViewportSize((previousSize) =>
                    {
                        const nextSize = readViewportSize();

                        if (shouldSkipViewportUpdate(previousSize, nextSize))
                        {
                            return previousSize;
                        }

                        return nextSize;
                    });
                }, 120);
            }
            catch (e)
            {}
        };

        window.addEventListener('resize', onResize);
        return () =>
        {
            window.removeEventListener('resize', onResize);
            if (resizeCommitTimer) clearTimeout(resizeCommitTimer);
        };
    }, []);

    useEffect(() =>
    {
        try
        {
            const audio = createPreloadedAudio(MAIN_BUTTON_HOVER_AUDIO_SRC,
            {
                baseVolume: 0.3,
                loop: false,
                applyMaster: true,
            });
            hoverAudioRef.current = audio;
        }
        catch (e)
        {
            hoverAudioRef.current = null;
        }

        return () =>
        {
            if (!hoverAudioRef.current) return;
            try
            {
                hoverAudioRef.current.pause();
            }
            catch (e)
            {}
            try
            {
                hoverAudioRef.current.src = '';
            }
            catch (e)
            {}
            hoverAudioRef.current = null;
        };
    }, []);

    const playButtonHoverSound = () =>
    {
        if (isCoarsePointer) return;

        try
        {
            const cachedAudio = hoverAudioRef.current;
            const audio = cachedAudio && typeof cachedAudio.cloneNode === 'function' ?
                cachedAudio.cloneNode(true) :
                createPreloadedAudio(MAIN_BUTTON_HOVER_AUDIO_SRC,
                {
                    baseVolume: 0.6,
                    loop: false,
                    applyMaster: true,
                });
            if (!audio) return;
            audio._baseVolume = 0.6;
            try
            {
                masterVolume.applyToElement(audio);
            }
            catch (e)
            {}
            try
            {
                audio.currentTime = 0;
            }
            catch (e)
            {}
            try
            {
                audio.play().catch(() =>
                {});
            }
            catch (e)
            {}
        }
        catch (e)
        {}
    };

    const leaveTo = (to) =>
    {
        if (leaving) return;
        setLeaving(true);
        setTimeout(() =>
        {
            try
            {
                navigate(to);
            }
            catch (e)
            {}
        }, MAIN_EXIT_DURATION_MS);
    };

    const handleReturnToRoot = () =>
    {
        try
        {
            window.dispatchEvent(new CustomEvent('main-theme-fade-out',
            {
                detail:
                {
                    durationMs: MAIN_EXIT_DURATION_MS
                }
            }));
        }
        catch (e)
        {}
        leaveTo('/');
    };

    const preloadAboutMeAssets = async () =>
    {
        try
        {
            const cachedConfig = window.__aw24AboutMeAudioConfig;
            const config = await (async () =>
            {
                try
                {
                    const response = await fetch(ABOUT_ME_AUDIO_CONFIG_URL,
                    {
                        cache: 'no-store'
                    });
                    const data = await response.json();
                    try
                    {
                        window.__aw24AboutMeAudioConfig = data;
                    }
                    catch (e)
                    {}
                    return data;
                }
                catch (e)
                {
                    return cachedConfig ||
                    {
                        tabs:
                        {}
                    };
                }
            })();

            const tabs = config && config.tabs && typeof config.tabs === 'object' ? Object.values(config.tabs) : [];
            const audioSources = Array.from(new Set(
                tabs.flatMap((tab) =>
                {
                    const sources = [];
                    if (tab && tab.music) sources.push(tab.music);
                    if (tab && tab.clickSfx) sources.push(tab.clickSfx);
                    return sources;
                }).filter(Boolean)
            ));

            if (!window.__aw24AboutMePreloadedAudio) window.__aw24AboutMePreloadedAudio = {};
            if (!window.__aw24AboutMePreloadedImages) window.__aw24AboutMePreloadedImages = {};

            audioSources.forEach((src) =>
            {
                try
                {
                    if (window.__aw24AboutMePreloadedAudio[src]) return;
                    const audio = createPreloadedAudio(src,
                    {
                        baseVolume: 1,
                        loop: false,
                        applyMaster: false,
                    });
                    if (!audio) return;
                    window.__aw24AboutMePreloadedAudio[src] = audio;
                }
                catch (e)
                {}
            });

            ABOUT_ME_IMAGE_SOURCES.forEach((src) =>
            {
                try
                {
                    if (window.__aw24AboutMePreloadedImages[src]) return;
                    const image = new Image();
                    image.decoding = 'async';
                    image.src = src;
                    window.__aw24AboutMePreloadedImages[src] = image;
                }
                catch (e)
                {}
            });
        }
        catch (e)
        {}
    };

    const preloadFullMoonClubAssets = async () =>
    {
        try
        {
            const cachedConfig = window.__aw24FullMoonClubAudioConfig;
            const config = await (async () =>
            {
                try
                {
                    const response = await fetch(FULL_MOON_CLUB_AUDIO_CONFIG_URL,
                    {
                        cache: 'no-store'
                    });
                    const data = await response.json();
                    try
                    {
                        window.__aw24FullMoonClubAudioConfig = data;
                    }
                    catch (e)
                    {}
                    return data;
                }
                catch (e)
                {
                    return cachedConfig ||
                    {};
                }
            })();

            if (!window.__aw24FullMoonClubPreloadedAudio) window.__aw24FullMoonClubPreloadedAudio = {};

            [config && config.music, config && config.intro]
            .filter(Boolean)
                .forEach((src) =>
                {
                    try
                    {
                        if (window.__aw24FullMoonClubPreloadedAudio[src]) return;
                        const audio = createPreloadedAudio(src,
                        {
                            baseVolume: 1,
                            loop: false,
                            applyMaster: false,
                        });
                        if (!audio) return;
                        window.__aw24FullMoonClubPreloadedAudio[src] = audio;
                    }
                    catch (e)
                    {}
                });
        }
        catch (e)
        {}
    };

    // Load class list from public/siteGoobers.json
    useEffect(() =>
    {
        let cancelled = false;
        const load = async () =>
        {
            try
            {
                const res = await fetch('/siteGoobers.json');
                const data = await res.json();
                if (!cancelled && Array.isArray(data)) setGoobers(data);
            }
            catch (e)
            {
                if (!cancelled) setGoobers([]);
            }
        };
        load();
        return () =>
        {
            cancelled = true;
        };
    }, []);

    // Select class theme with persistence; reroll only when coming from '/'
    useEffect(() =>
    {
        if (!goobers || !goobers.length) return;

        const KEY = 'aw24-main-selected-class';
        const pickRandom = () =>
        {
            const maxAttempts = 20;
            let selected = null;
            for (let attempt = 0; attempt <maxAttempts && !selected; attempt++)
            {
                const hits = [];
                for (const g of goobers)
                {
                    const raw = Number(g.chance);
                    const p = isFinite(raw) ? (raw > 1 ? raw / 100 : Math.max(0, Math.min(1, raw))) : 0;
                    if (Math.random() <p) hits.push(g);
                }
                if (hits.length > 0)
                {
                    selected = hits[Math.floor(Math.random() * hits.length)];
                }
            }
            return selected || goobers[0];
        };

        const applyHalloweenAri = (selected) =>
        {
            if (!HALLOWEEN_MODE || !selected || selected.name !== 'Ari') return selected;
            return goobers.find((goober) => goober && goober.name === 'Hellhound Ari') || selected;
        };

        const prev = (window.history && window.history.state && window.history.state.from) || null;

        // If entering /main from '/', reroll and persist
        if (prev === '/')
        {
            const selected = applyHalloweenAri(pickRandom());
            setChosen(selected);
            try
            {
                localStorage.setItem(KEY, selected && selected.name ? selected.name : '');
            }
            catch (e)
            {}
            return;
        }

        // Otherwise, keep previously selected class (restore from storage if available)
        let savedName = null;
        try
        {
            savedName = localStorage.getItem(KEY);
        }
        catch (e)
        {
            savedName = null;
        }
        if (savedName)
        {
            const foundName = HALLOWEEN_MODE && savedName === 'Ari' ? 'Hellhound Ari' : savedName;
            const found = goobers.find(g => g && g.name === foundName);
            if (found)
            {
                setChosen(found);
                return;
            }
        }
        // No saved selection yet — pick once and persist for future visits
        const selected = applyHalloweenAri(pickRandom());
        setChosen(selected);
        try
        {
            localStorage.setItem(KEY, selected && selected.name ? selected.name : '');
        }
        catch (e)
        {}
    }, [goobers]);

    // When animation begins, announce selected class for music selection
    useEffect(() =>
    {
        if (!animate) return;

        // Returning from /discord should preserve the current main loop exactly as-is.
        const prev = (window.history && window.history.state && window.history.state.from) || null;
        if (prev === '/discord') return;

        const payload = chosen ||
        {
            name: 'Default',
            music: '/Audio/Main-Loop.ogg'
        };
        try
        {
            window.dispatchEvent(new CustomEvent('main-class-selected',
            {
                detail: payload
            }));
        }
        catch (e)
        {}
    }, [animate, chosen]);

    const handleNoRedirectPress = (e) =>
    {
        try
        {
            const sfx = new Audio('/Audio/ButtonNoRedirect.wav');
            sfx._baseVolume = 1.0;
            try
            {
                masterVolume.applyToElement(sfx);
            }
            catch (err)
            {}
            try
            {
                sfx.play();
            }
            catch (err)
            {}
        }
        catch (err)
        {}
        try
        {
            const btn = e && e.currentTarget ? e.currentTarget : null;
            if (!btn) return;
            if (btn.dataset && btn.dataset.shakeActive === '1') return;
            if (btn.dataset) btn.dataset.shakeActive = '1';
            const originalBg = btn.style.background;
            const originalBorder = btn.style.border;
            const originalPosition = btn.style.position;
            const originalLeft = btn.style.left;
            const originalTop = btn.style.top;
            const originalTransition = btn.style.transition;
            btn.style.background = 'rgb(24, 0, 0)';
            btn.style.border = '1px solid #ff0000';
            const duration = 200;
            const intervalMs = 22;
            let elapsed = 0;
            btn.style.position = 'relative';
            btn.style.transition = 'none';
            const baseAmpX = 1 + Math.random() * 4; // 6-14px jitter range
            const baseAmpY = 1 + Math.random() * 4; // 1-4px jitter range
            const intervalId = setInterval(() =>
            {
                elapsed += intervalMs;
                // random jitter each tick for unpredictability
                const offsetX = (Math.random() * 2 - 1) * baseAmpX;
                const offsetY = (Math.random() * 2 - 1) * baseAmpY;
                btn.style.left = offsetX.toFixed(1) + 'px';
                btn.style.top = offsetY.toFixed(1) + 'px';
                if (elapsed >= duration)
                {
                    clearInterval(intervalId);
                    // restore styles
                    btn.style.left = originalLeft;
                    btn.style.top = originalTop;
                    btn.style.position = originalPosition;
                    btn.style.transition = originalTransition;
                    btn.style.background = originalBg;
                    btn.style.border = originalBorder;
                    if (btn.dataset) btn.dataset.shakeActive = '0';
                }
            }, intervalMs);
        }
        catch (err)
        {}
    };

    const items = [
        // if a button doesnt have any re direction, it should have handleNoRedirectPress as onClick
        // TEMPLATE:
        // { label: 'LABEL', onClick: handleNoRedirectPress, className: 'btn-primary' },

        {
            label: 'ABOUT ME',
            onClick: () =>
            {
                try
                {
                    window.__aw24AboutMeEntryTransition = {
                        enabled: true,
                        requestedAt: Date.now()
                    };
                }
                catch (e)
                {}
                try
                {
                    preloadAboutMeAssets();
                }
                catch (e)
                {}
                leaveTo('/AboutMe');
            },
            className: 'btn-primary'
        },
        {
            label: 'PROJECTS',
            onClick: handleNoRedirectPress,
            className: 'btn-primary'
        },
        {
            label: 'YOUTUBE',
            onClick: () =>
            {
                try
                {
                    window.open('https://www.youtube.com/@arielwolf24', '_blank', 'noopener,noreferrer');
                }
                catch (e)
                {}
            },
            className: 'btn-primary'
        },
        {
            label: 'STEAM',
            onClick: () =>
            {
                try
                {
                    window.open('https://steamcommunity.com/id/arielwolf24', '_blank', 'noopener,noreferrer');
                }
                catch (e)
                {}
            },
            className: 'btn-primary'
        },
        {
            label: 'DISCORD',
            onClick: () => leaveTo('/discord'),
            className: 'btn-primary'
        },
        {
            label: 'FURAFFINITY',
            onClick: () => navigate('/FurAffinityWarning'),
            className: 'btn-primary'
        },
        {
            label: 'RGL',
            onClick: () => window.dispatchEvent(new CustomEvent('enter-rgl')),
            className: 'btn-primary'
        },
        {
            label: 'FULL MOON CLUB',
            onClick: () =>
            {
                try
                {
                    preloadFullMoonClubAssets();
                }
                catch (e)
                {}
                leaveTo('/FullMoonClub');
            },
            className: 'btn-primary'
        },
        {
            label: 'SETTINGS',
            onClick: handleNoRedirectPress,
            className: 'btn-primary'
        },
        {
            label: 'RETURN',
            onClick: handleReturnToRoot,
            className: 'btn-primary btn-danger'
        },
    ];

    return ( <div style = {
            {
                position: 'fixed',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
                pointerEvents: leaving ? 'none' : 'auto',
                opacity: visible ? 1 : 0,
                transition: leaving ? 'none' : 'opacity 360ms ease'
            }
        } >
        {
            showAssetStage && ( <div style = {
                    {
                        position: 'fixed',
                        right: 0,
                        top: '50%',
                        transform: leaving ?
                            'translate(100%, -50%)' :
                            (animate ? 'translate(2%, -50%)' : 'translate(18%, -50%)'),
                        width: `${assetStageWidth}px`,
                        height: `${assetStageHeight}px`,
                        pointerEvents: 'none',
                        opacity: animate ? 1 : 0,
                        transition: leaving ?
                            `transform ${MAIN_EXIT_DURATION_MS}ms ease-in` :
                            'transform 700ms cubic-bezier(.19,1,.22,1) 120ms, opacity 420ms ease 120ms',
                        overflow: 'visible',
                        zIndex: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        willChange: leaving ? 'transform' : undefined,
                    }
                }
                aria-hidden = "true" >
                <div style = {
                    {
                        position: 'relative',
                        width: `${assetStageWidth}px`,
                        height: `${assetStageHeight}px`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        overflow: 'visible',
                        transform: `translate(${assetXOffset}px, ${assetYOffset}px)`,
                    }
                } >
                {
                    hasBgImage && ( <> </>
                    )
                }
                {
                    (hasBgImage || hasFloofImage) && ( <div style = {
                            {
                                position: 'absolute',
                                right: `${floofReferenceRightPx}px`,
                                top: `${floofReferenceTopPx}px`,
                                width: `${assetStageWidth}px`,
                                height: `${floofReferenceHeightPx}px`,
                                maxWidth: 'none',
                                maxHeight: 'none',
                                overflow: 'visible',
                                transform: animate ?
                                    `translate(${floofXOffset}px, calc(-50% + ${floofYOffset}px))` :
                                    `translate(${56 + floofXOffset}px, calc(-50% + ${floofYOffset}px))`,
                                transition: 'transform 760ms cubic-bezier(.19,1,.22,1) 240ms, opacity 520ms ease 240ms',
                            }
                        } >
                        {
                            hasBgImage && ( <img src = {
                                    chosen.bgImage
                                }
                                alt = ""
                                decoding = "async"
                                draggable = {
                                    false
                                }
                                style = {
                                    {
                                        position: 'absolute',
                                        right: `${bgReferenceRightPx}px`,
                                        top: `${BG_IMAGE_TOP_RATIO * 100}%`,
                                        transform: animate ? 'translateY(-50%) scale(1)' : 'translateY(-50%) scale(0.97)',
                                        width: 'auto',
                                        height: `${BG_IMAGE_HEIGHT_RATIO * 100}%`,
                                        maxWidth: 'none',
                                        maxHeight: 'none',
                                        objectFit: 'contain',
                                        objectPosition: 'right center',
                                        opacity: animate ? 0.72 : 0,
                                        transition: 'transform 760ms cubic-bezier(.19,1,.22,1) 180ms, opacity 520ms ease 180ms',
                                        willChange: 'transform, opacity',
                                    }
                                }
                                />
                            )
                        }
                        {
                            hasFloofImage && ( <img src = {
                                    chosen.floofImage
                                }
                                alt = ""
                                decoding = "async"
                                draggable = {
                                    false
                                }
                                style = {
                                    {
                                        position: 'absolute',
                                        right: 0,
                                        top: '50%',
                                        width: 'auto',
                                        height: '100%',
                                        maxWidth: 'none',
                                        maxHeight: 'none',
                                        objectFit: 'contain',
                                        objectPosition: 'right center',
                                        transform: 'translateY(-50%)',
                                        opacity: animate ? 1 : 0,
                                        transition: 'opacity 520ms ease 240ms',
                                        willChange: 'opacity',
                                    }
                                }
                                />
                            )
                        } </div>
                    )
                } <div style = {
                    {
                        position: 'absolute',
                        right: '18vw',
                        bottom: '5vh',
                        textAlign: 'right',
                        opacity: animate ? 1 : 0,
                        transform: animate ? 'translateX(0)' : 'translateX(28px)',
                        transition: 'transform 640ms cubic-bezier(.19,1,.22,1) 280ms, opacity 420ms ease 280ms',
                        zIndex: 1,
                        whiteSpace: 'nowrap',
                        minWidth: 'min(42vw, 560px)',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                    }
                } >
                <div style = {
                    {
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: hasCustomNameImage ? 'center' : 'flex-end',
                        gap: 'clamp(8px, 1.3vh, 16px)',
                        width: hasCustomNameImage ? `${scaledNameAssetWidthPx}px` : 'auto',
                        maxWidth: '100%',
                    }
                } >
                {
                    hasCustomNameImage ? ( <div style = {
                            {
                                width: `${scaledNameAssetWidthPx}px`,
                                height: `${scaledNameAssetHeightPx}px`,
                                flex: '0 0 auto',
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                maxWidth: '100%',
                                maxHeight: '100%',
                            }
                        } >
                        <img src = {
                            chosen.nameImage
                        }
                        alt = {
                            (chosen && chosen.name) ? `${chosen.name} nameplate` : 'Class nameplate'
                        }
                        decoding = "async"
                        draggable = {
                            false
                        }
                        style = {
                            {
                                width: '100%',
                                height: '100%',
                                objectFit: 'contain',
                                objectPosition: 'center bottom',
                                display: 'block',
                            }
                        }
                        /> </div>
                    ) : ( <span style = {
                            {
                                color: '#060606',
                                fontFamily: `'Valve Pulp', 'Segoe UI', sans-serif`,
                                fontSize: 'clamp(3rem, 6.2vw, 6.2rem)',
                                lineHeight: 0.9,
                                letterSpacing: '0.14em',
                                textTransform: 'uppercase',
                                textShadow: '0 8px 20px rgba(255, 255, 255, 0.14), 0 16px 28px rgba(0, 0, 0, 0.28)',
                            }
                        } >
                        {
                            (chosen && chosen.name) ? chosen.name : 'Class Name'
                        } </span>
                    )
                }

                {
                    qualitiesTextItems.length > 0 && ( <div style = {
                            {
                                display: 'flex',
                                flexDirection: 'row',
                                flexWrap: 'nowrap',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                columnGap: `${fixedQualitiesRowGapPx}px`,
                                rowGap: 'clamp(6px, 0.8vh, 10px)',
                                width: hasCustomNameImage ? '100%' : 'fit-content',
                                maxWidth: `${qualitiesRowMaxWidthPx}px`,
                                alignSelf: hasCustomNameImage ? 'stretch' : 'center',
                            }
                        } >
                        {
                            qualitiesTextItems.map((text, index) =>
                            {
                                const rotationDeg = Number.isFinite(qualitiesTextRotations[index]) ? qualitiesTextRotations[index] : 0;

                                return ( <div key = {
                                        `${chosen && chosen.name ? chosen.name : 'class'}-quality-${index}-${text}`
                                    }
                                    style = {
                                        {
                                            position: 'relative',
                                            width: 'fit-content',
                                            maxWidth: '100%',
                                            flex: '0 1 auto',
                                            padding: `${fixedQualitiesTextPaddingY}px ${fixedQualitiesTextPaddingX}px`,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                        }
                                    } >
                                    <div aria-hidden = "true"
                                    style = {
                                        {
                                            position: 'absolute',
                                            inset: 0,
                                            background: chosenQualitiesBg,
                                            border: 'none',
                                            borderRadius: 0,
                                            transform: `rotate(${rotationDeg}deg)`,
                                            transformOrigin: 'center',
                                            transition: 'none',
                                            boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
                                        }
                                    }
                                    /> <span style = {
                                        {
                                            position: 'relative',
                                            zIndex: 1,
                                            color: '#ffffff',
                                            fontFamily: `'Valve Pulp', 'Segoe UI', sans-serif`,
                                            fontSize: `${fixedQualitiesTextFontSizePx}px`,
                                            lineHeight: 1,
                                            letterSpacing: '0.08em',
                                            textTransform: 'uppercase',
                                            textAlign: 'center',
                                            whiteSpace: 'nowrap',
                                            textShadow: '0 2px 8px rgba(0, 0, 0, 0.42)',
                                        }
                                    } >
                                    {
                                        text
                                    } </span> </div>
                                );
                            })
                        } </div>
                    )
                } </div> </div> </div> </div>
            )
        }

        {
            /* Left-side persistent button list */ } <div style = {
            {
                position: 'fixed',
                left: `calc(clamp(16px, 3vw, 40px) + ${BUTTON_LIST_LEFT_OFFSET})`,
                top: '50%',
                width: scaledButtonListWidth,
                height: scaledButtonListHeight,
                transform: leaving ? `translate(calc(-100vw - ${scaledButtonListWidth}px), -50%)` : 'translateY(-50%)',
                transformOrigin: 'left center',
                transition: leaving ? `transform ${MAIN_BUTTON_EXIT_DURATION_MS}ms ease-in` : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                pointerEvents: leaving ? 'none' : 'auto',
                zIndex: 2,
                willChange: leaving ? 'transform' : undefined
            }
        } >
        <div style = {
            {
                width: scaledButtonListWidth,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: scaledButtonGap
            }
        } >
        {
            items.map((item, idx) =>
            {
                const delayS = idx * BUTTON_AppearDelay;
                const initial = {
                    opacity: 0,
                    transform: 'translateX(-44px)'
                };
                const final = {
                    opacity: 1,
                    transform: 'translateX(0)'
                };
                const transition = `opacity 420ms ease ${delayS}s, transform 420ms ease ${delayS}s, background 240ms ease 0s`;
                const style = {
                    width: '100%',
                    padding: `${scaledButtonPaddingY}px ${scaledButtonPaddingX}px`,
                    fontSize: `${scaledButtonFontSizePx}px`,
                    letterSpacing: `${scaledButtonLetterSpacingPx}px`,
                    borderWidth: `${Math.max(1, buttonListScale)}px`,
                    WebkitBackdropFilter: isCoarsePointer ? 'none' : undefined,
                    backdropFilter: isCoarsePointer ? 'none' : undefined,
                    transition,
                    ...(animate ? final : initial),
                    pointerEvents: (animate && !leaving) ? 'auto' : 'none',
                };
                return ( <button type = "button"
                    key = {
                        item.label
                    }
                    onClick = {
                        item.onClick
                    }
                    onMouseEnter = {
                        playButtonHoverSound
                    }
                    className = {
                        item.className
                    }
                    style = {
                        style
                    } >
                    {
                        item.label
                    } </button>
                );
            })
        } </div> </div>

        {
            /* Center message remains centered */ } <div style = {
            {
                textAlign: 'center',
                color: '#fff',
                padding: 24,
                position: 'relative',
                zIndex: 1,
                opacity: leaving ? 0 : 1,
                transition: `opacity ${MAIN_EXIT_DURATION_MS}ms ease`
            }
        } >
        <h1 style = {
            {
                margin: 0,
                fontSize: '1.6rem',
                fontFamily: `'Valve Pulp', 'Segoe UI', sans-serif`
            }
        } > /main is a little less empty for now, more coming soon!</h1 >
        </div>

        {
            /* Debug label: shows selected class theme in bottom-right */ }
        {
            animate && ( <div style = {
                    {
                        position: 'fixed',
                        right: 12,
                        bottom: 10,
                        color: '#ffffffcc',
                        fontSize: '0.95rem',
                        letterSpacing: '.3px',
                        pointerEvents: 'none'
                    }
                } >
                {
                    (chosen && chosen.name) ? `${chosen.name}'s Theme` : 'Loading Theme…'
                } </div>
            )
        } </div>
    );
}
